/* DRAFT — PENDING LEGAL REVIEW */
import React from "react";
import PageShell from "../components/layout/PageShell";
import Container from "../components/primitives/Container";
import Section from "../components/primitives/Section";
import Eyebrow from "../components/primitives/Eyebrow";
import Reveal from "../components/primitives/Reveal";
import SeoHead from "../components/seo/SeoHead";
import DraftBanner from "../components/legal/DraftBanner";

const H2 = ({ children }) => (
  <h2
    className="mt-12 font-serif font-normal text-navy text-balance"
    style={{ fontSize: "clamp(26px, 3vw, 36px)", lineHeight: 1.2, letterSpacing: "-0.005em" }}
  >
    {children}
  </h2>
);

const P = ({ children, ...rest }) => (
  <p
    {...rest}
    className="mt-6 font-sans text-[16px] sm:text-[16.5px] leading-[1.75] text-navy/80 max-w-prose"
  >
    {children}
  </p>
);

const Terms = () => {
  return (
    <PageShell>
      <SeoHead
        title="Terms of Use"
        description="Terms governing your access to and use of the Quantum US Capital Partners website."
        path="/legal/terms"
      >
        <meta name="x-document-status" content="draft-pending-legal-review" />
      </SeoHead>

      <Section tone="light" className="pt-28 md:pt-32 lg:pt-36 pb-20 md:pb-24">
        <Container>
          <Reveal className="max-w-3xl">
            <DraftBanner />
            <div className="mt-8">
              <Eyebrow tone="muted">Legal</Eyebrow>
            </div>
            <h1
              className="mt-8 font-serif font-normal text-navy text-balance"
              data-testid="terms-title"
              style={{ fontSize: "clamp(40px, 5.5vw, 80px)", lineHeight: 1.05, letterSpacing: "-0.01em" }}
            >
              Terms of Use.
            </h1>
            <span aria-hidden="true" className="mt-10 block h-px w-12 bg-gold" />
            <p
              className="mt-10 font-sans uppercase text-navy/55"
              style={{ fontSize: "11px", letterSpacing: "0.22em" }}
              data-testid="terms-effective"
            >
              Effective February 2026
            </p>
          </Reveal>

          <Reveal className="mt-10 max-w-3xl">
            <P>
              These Terms of Use govern your access to and use of the
              quscap.com website (the &ldquo;Site&rdquo;), operated by Quantum
              US Capital Partners LLC (&ldquo;Quantum&rdquo;). By using the
              Site you accept these terms; if you do not accept them, you
              should not use the Site.
            </P>

            <H2>Acceptance</H2>
            <P data-testid="terms-acceptance">
              By accessing or using this website, you agree to be bound by
              these Terms of Use. If you do not agree, please discontinue use
              of the website. Continued use following any update to these
              terms constitutes acceptance of the revised terms.
            </P>

            <H2>Regulatory Status</H2>
            <P data-testid="terms-regulatory-statement">
              <strong className="font-medium text-navy">
                Quantum US Capital Partners LLC is a private investment sponsor
                and strategic investment firm. It is not a regulated
                investment manager, an alternative investment fund manager
                (AIFM), an investment adviser, or a broker-dealer, and does
                not manage client assets, solicit subscriptions to any
                regulated fund, or hold itself out as any of the foregoing.
              </strong>{" "}
              Investment activities described on the Site, where conducted
              under regulated frameworks, are undertaken through affiliated
              entities authorised in the relevant jurisdiction. Nothing on
              the Site constitutes, or should be construed as, an offer to
              sell or a solicitation of an offer to buy any security or
              interest in any vehicle, in any jurisdiction in which such an
              offer or solicitation would be unlawful.
            </P>

            <H2>No Investment Advice</H2>
            <P>
              The Site is published for general informational purposes. None
              of the material on the Site constitutes investment, legal,
              accounting, tax, or other professional advice, nor does any of
              it amount to a recommendation to acquire, hold, or dispose of
              any security or interest. You should consult your own advisers
              before making any decision that depends on the matters
              discussed on the Site.
            </P>

            <H2>Forward-Looking Statements</H2>
            <P>
              Certain material on the Site &mdash; including in the Quantum
              Letters and elsewhere &mdash; contains observations about the
              future. Such statements are inherently uncertain. Actual
              outcomes may differ materially from those expressed or implied.
              We do not undertake any obligation to update forward-looking
              statements following publication.
            </P>

            <H2>Confidentiality of Inquiries</H2>
            <P>
              Inquiries submitted through the Contact page are received in
              confidence and used for the purposes described in our Privacy
              Policy. Submission of an inquiry does not constitute the
              establishment of any professional, advisory, fiduciary, or
              client relationship between you and Quantum.
            </P>

            <H2>Intellectual Property</H2>
            <P>
              The Site, including its design, text, imagery, structure, and
              the Quantum Letters, is the property of Quantum or its
              licensors and is protected by applicable intellectual property
              laws. You may view and print individual pages for personal,
              non-commercial reference; any other reproduction, republication,
              distribution, or commercial use requires prior written
              permission.
            </P>

            <H2>Acceptable Use</H2>
            <P>
              You agree not to use the Site in any manner that could damage,
              disable, overburden, or impair it, that interferes with any
              other party&rsquo;s use of it, or that attempts to access
              areas not intended for public use. Automated collection of
              information without prior written consent is prohibited.
            </P>

            <H2>Third-Party Links</H2>
            <P>
              The Site may include links to third-party properties for
              convenience. Quantum does not control, endorse, or assume
              responsibility for the content, policies, or practices of any
              third-party property.
            </P>

            <H2>Disclaimer of Warranties</H2>
            <P>
              The Site is provided on an &ldquo;as is&rdquo; and &ldquo;as
              available&rdquo; basis. To the maximum extent permitted by
              applicable law, Quantum disclaims all warranties, express or
              implied, including warranties of merchantability, fitness for a
              particular purpose, and non-infringement. We do not warrant
              that the Site will be uninterrupted, error-free, or free of
              harmful components.
            </P>

            <H2>Limitation of Liability</H2>
            <P>
              To the maximum extent permitted by applicable law, Quantum and
              its affiliates, partners, principals, employees, and agents
              shall not be liable for any indirect, incidental, special,
              consequential, or exemplary damages arising out of or in
              connection with your use of the Site, even if advised of the
              possibility of such damages.
            </P>

            <H2>Changes to These Terms</H2>
            <P>
              We may revise these Terms of Use from time to time. The
              effective date above will be updated accordingly. Continued use
              of the Site following any revision constitutes acceptance of
              the revised terms.
            </P>

            <H2>Contact</H2>
            <P>
              Inquiries regarding these Terms of Use may be addressed to the
              Office of the Chairman at{" "}
              <a
                href="mailto:chairman@quscap.com"
                className="text-navy underline decoration-gold underline-offset-4 hover:text-gold transition-colors"
                data-testid="terms-contact-email"
              >
                chairman@quscap.com
              </a>
              .
            </P>
          </Reveal>
        </Container>
      </Section>
    </PageShell>
  );
};

export default Terms;
